import * as React from 'react';
import { Check } from 'lucide-react';
import { cn } from '../../lib/cn';

/** The progress state of a single step in a {@link Stepper}. */
export type StepperStepStatus = 'done' | 'active' | 'upcoming';

/** One step in the horizontal {@link Stepper} rail. */
export interface StepperStep {
  /** Stable identifier (used as the React key and for test assertions). */
  id: string;
  /** Already-translated label shown under the marker. No i18n here — the caller resolves copy. */
  label: string;
  status: StepperStepStatus;
}

export interface StepperProps {
  steps: StepperStep[];
  /**
   * Accessible name for the progress rail, announced by screen readers.
   * Already-translated — the component stays i18n-free, so the caller resolves
   * the copy (e.g. `t('wp.migration.wizard.progress_label')`). Omit for an
   * unnamed list.
   */
  ariaLabel?: string;
  className?: string;
}

/**
 * A horizontal progress rail for a multi-step wizard. Purely presentational
 * and i18n-free: the caller passes already-translated `label`s and owns the
 * step-status machine. One canonical stepper for the whole app — do not
 * hand-roll a second numbered-rail implementation (the WagePro setup and
 * migration wizards both consolidate onto this).
 *
 * Colours come exclusively from named design tokens (`--accent`, `--line`,
 * `--ink`, `--ink-muted`, `--color-success`) — no arbitrary hex/rgba values,
 * matching the glass design system.
 */
export function Stepper({
  steps,
  ariaLabel,
  className,
}: StepperProps): React.JSX.Element {
  return (
    <ol
      className={cn('flex w-full items-start', className)}
      aria-label={ariaLabel}
    >
      {steps.map((step, index) => {
        const isDone = step.status === 'done';
        const isActive = step.status === 'active';
        const isLast = index === steps.length - 1;
        return (
          <li
            key={step.id}
            className="flex flex-1 flex-col items-center"
            aria-current={isActive ? 'step' : undefined}
          >
            <div className="flex w-full items-center">
              {/* Leading connector (hidden for the first step). */}
              <span
                aria-hidden
                className={cn(
                  'h-0.5 flex-1',
                  index === 0 && 'invisible',
                  isDone || isActive
                    ? 'bg-[color:var(--accent)]'
                    : 'bg-[color:var(--line)]',
                )}
              />
              <span
                aria-hidden
                className={cn(
                  'flex h-7 w-7 shrink-0 items-center justify-center rounded-full border text-xs font-semibold',
                  isDone &&
                    'border-[color:var(--color-success)] bg-[color:var(--color-success)] text-white',
                  isActive &&
                    'border-[color:var(--accent)] bg-[color:var(--accent)] text-white',
                  !isDone &&
                    !isActive &&
                    'border-[color:var(--line)] text-[color:var(--ink-muted)]',
                )}
              >
                {isDone ? <Check className="h-4 w-4" aria-hidden /> : index + 1}
              </span>
              {/* Trailing connector (hidden for the last step). */}
              <span
                aria-hidden
                className={cn(
                  'h-0.5 flex-1',
                  isLast && 'invisible',
                  isDone
                    ? 'bg-[color:var(--accent)]'
                    : 'bg-[color:var(--line)]',
                )}
              />
            </div>
            <span
              className={cn(
                'mt-2 px-1 text-center text-xs',
                isActive
                  ? 'font-semibold text-[color:var(--ink)]'
                  : 'text-[color:var(--ink-muted)]',
              )}
            >
              {step.label}
            </span>
          </li>
        );
      })}
    </ol>
  );
}
